import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { useDebounceValue } from 'usehooks-ts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTheme } from '@/components/providers/theme-provider';
import { useEffect, useMemo } from 'react';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onNextPage: () => void;
  onPreviousPage: () => void;
  nextPageDisabled: boolean;
  previousPageDisabled: boolean;
  onSearch: (value: string) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onNextPage,
  onPreviousPage,
  nextPageDisabled,
  previousPageDisabled,
  onSearch,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue('', 100);

  useEffect(() => {
    onSearch(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const mobileBreakpoint = 800;
  const dynamicColumns = useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (windowWidth < mobileBreakpoint) {
      // @ts-expect-error Missing hiddenOnMobile
      return columns.filter((column) => !column.meta?.hiddenOnMobile);
    }
    return columns;
  }, [windowWidth, columns]);

  const table = useReactTable({
    data,
    columns: dynamicColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const { theme } = useTheme();

  return (
    <div className='w-full'>
      <Input
        placeholder='Type to search by identifier, e.g pax'
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className={`max-w-sm ${theme === 'dark' ? 'text-white' : 'text-black'}`}
      />
      <div className='flex gap-4 flex-wrap'>
        <DropdownMenu>
          <DropdownMenuContent align='end'>
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className='capitalize'
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className='mt-8'>
        <div className='rounded-md border'>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='h-24 text-center'
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className='flex items-center justify-end space-x-2 py-4'>
          <div className='space-x-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={onPreviousPage}
              disabled={previousPageDisabled}
            >
              Previous
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={onNextPage}
              disabled={nextPageDisabled}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
